import React, { useState } from 'react';
import { bool, func, node, string } from 'prop-types';
import classNames from 'classnames';
import { BiImageAdd } from 'react-icons/bi';

import { FormattedMessage } from '../../util/reactIntl';

import css from './ImageDropzone.css';

// Same matching rules as the input's accept attribute: exact MIME types,
// wildcards such as image/* and file extensions such as .png
const fileMatchesAccept = (file, accept) => {
  const rules = (accept || '')
    .split(',')
    .map(rule => rule.trim().toLowerCase())
    .filter(Boolean);
  if (rules.length === 0) {
    return true;
  }
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return rules.some(rule => {
    if (rule.startsWith('.')) {
      return name.endsWith(rule);
    }
    if (rule.endsWith('/*')) {
      return type.startsWith(rule.slice(0, -1));
    }
    return type === rule;
  });
};

const firstAcceptedFile = (files, accept) =>
  Array.from(files || []).find(file => fileMatchesAccept(file, accept));

/**
 * A drop zone for a single image: drag and drop a file onto it, or click it
 * to browse. Calls onFileSelected with the first file that matches accept.
 */
const ImageDropzone = props => {
  const { id, onFileSelected, accept, disabled, title, hint, className } = props;
  const [isActive, setIsActive] = useState(false);

  // preventDefault marks the zone as a drop target and stops the browser from
  // opening a file dropped on it
  const handleDragOver = e => {
    e.preventDefault();
    if (!disabled) {
      setIsActive(true);
    }
  };

  const handleDragLeave = e => {
    // dragleave also fires when the pointer moves onto a child element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsActive(false);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsActive(false);
    if (disabled) {
      return;
    }
    const file = firstAcceptedFile(e.dataTransfer && e.dataTransfer.files, accept);
    if (file) {
      onFileSelected(file);
    }
  };

  const handleChange = e => {
    const file = firstAcceptedFile(e.target.files, accept);
    // Reset so picking the same file again fires another change
    e.target.value = '';
    if (file) {
      onFileSelected(file);
    }
  };

  const titleContent = title || (
    <FormattedMessage
      id="ImageDropzone.title"
      values={{ b: (...chunks) => <span className={css.browse}>{chunks}</span> }}
    />
  );

  const classes = classNames(css.root, className, {
    [css.active]: isActive,
    [css.disabled]: disabled,
  });

  return (
    <div
      className={classes}
      onDragEnter={handleDragOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        id={id}
        className={css.input}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={handleChange}
      />
      <label htmlFor={id} className={css.label}>
        <BiImageAdd className={css.icon} aria-hidden="true" />
        <span className={css.title}>{titleContent}</span>
        {hint ? <span className={css.hint}>{hint}</span> : null}
      </label>
    </div>
  );
};

ImageDropzone.defaultProps = {
  accept: 'image/png,image/jpeg',
  disabled: false,
  title: null,
  hint: null,
  className: null,
};

ImageDropzone.propTypes = {
  id: string.isRequired,
  onFileSelected: func.isRequired,
  accept: string,
  disabled: bool,
  title: node,
  hint: node,
  className: string,
};

export default ImageDropzone;
